import { useCallback, useRef, useState } from "react";
import { Upload, FileText, Loader2, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const UploadZone = ({ onFile, onLoadSample, loading }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) onFile(file);
    },
    [onFile]
  );

  return (
    <div className="w-full">
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "relative cursor-pointer border border-dashed rounded-sm px-8 py-16 text-center select-none",
          "bg-[#0f172a] border-[#1e293b] hover:border-blue-500/60",
          dragOver && "border-blue-500 bg-[#1e293b]"
        )}
        style={{ transition: "border-color 0.15s ease, background-color 0.15s ease" }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="hidden"
          data-testid="upload-file-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
        />

        <div className="flex flex-col items-center gap-4">
          <div className="h-14 w-14 border border-[#1e293b] rounded-sm flex items-center justify-center bg-[#020617]">
            {loading ? (
              <Loader2 className="h-6 w-6 text-blue-400 animate-spin" />
            ) : (
              <Upload className="h-6 w-6 text-blue-400" />
            )}
          </div>

          <div className="space-y-2">
            <div className="text-xs tracking-[0.2em] uppercase text-slate-400 font-mono">
              [ INPUT&nbsp;/&nbsp;CSV ]
            </div>
            <h3 className="text-xl font-semibold text-slate-50">
              {loading ? "Analyzing rules…" : "Drop firewall policy CSV here"}
            </h3>
            <p className="text-sm text-slate-400 font-mono">
              or click to browse · required:{" "}
              <span className="text-slate-300">rule_id, name, action, source, destination</span>{" "}
              + <span className="text-slate-300">services</span> (or{" "}
              <span className="text-slate-300">protocol</span> +{" "}
              <span className="text-slate-300">port</span>)
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              data-testid="upload-browse-btn"
              variant="outline"
              className="rounded-sm border-[#1e293b] bg-[#020617] hover:bg-[#1e293b] text-slate-100"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
            >
              <FileText className="h-4 w-4 mr-2" />
              Choose file
            </Button>
            <Button
              data-testid="load-sample-btn"
              variant="ghost"
              className="rounded-sm text-blue-300 hover:text-blue-200 hover:bg-blue-500/10"
              onClick={(e) => {
                e.stopPropagation();
                onLoadSample();
              }}
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Load sample data
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
